import React from 'react';
import { useTranslation } from 'react-i18next';

interface PrivacyPolicyViewerProps {
  onClose?: () => void;
  mode?: 'modal' | 'page';
}

export const PrivacyPolicyViewer: React.FC<PrivacyPolicyViewerProps> = ({ onClose, mode = 'modal' }) => {
  const { t } = useTranslation('privacy');

  const renderSection = (section: any, index: number) => {
    return (
      <div key={index} className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
        
        {section.content && (
          <p className="mb-4 leading-relaxed text-gray-700">{section.content}</p>
        )}
        
        {section.items && (
          <ul className="list-disc ml-6 space-y-2 mb-4">
            {section.items.map((item: string, idx: number) => (
              <li key={idx} className="text-gray-700">{item}</li>
            ))}
          </ul>
        )}
        
        {section.subsections && section.subsections.map((subsection: any, subIdx: number) => (
          <div key={subIdx} className="ml-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{subsection.title}</h3>
            
            {subsection.content && (
              <p className="mb-3 leading-relaxed text-gray-700">{subsection.content}</p>
            )}
            
            {subsection.items && (
              <ul className="list-disc ml-6 space-y-1">
                {subsection.items.map((item: string, itemIdx: number) => (
                  <li key={itemIdx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        
        {section.contact && (
          <div className="ml-4 mt-3 p-4 bg-gray-50 rounded-lg">
            {section.contact.officer && (
              <p className="text-gray-700"><strong>{section.contact.officer}</strong></p>
            )}
            <p className="text-gray-700"><strong>{section.contact.company}</strong></p>
            <p className="text-gray-700">{section.contact.email}</p>
            <p className="text-gray-700">{section.contact.phone}</p>
            <p className="text-gray-700">{section.contact.address}</p>
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div className="text-gray-700">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
        <p className="text-sm text-gray-600">{t('version')}</p>
        <p className="text-sm text-gray-600">{t('lastUpdated')}</p>
      </div>
      
      {t('sections', { returnObjects: true }) && 
        (t('sections', { returnObjects: true }) as any[]).map((section, index) => 
          renderSection(section, index)
        )
      }
      
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600 italic">{t('footer')}</p>
      </div>
    </div>
  );

  if (mode === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">{t('title')}</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            {content}
          </div>
        </div>
      </div>
    );
  }

  // Page mode
  return (
    <div className="max-w-4xl mx-auto">
      {content}
    </div>
  );
};
